import { useNavigate, useRouterState } from "@tanstack/react-router";
import { Building2, Phone, Video } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { getAttribution } from "@/lib/attribution";

import { submitBookingRequest } from "@/lib/bookings.functions";
import { FIRM, FIRM_ADDRESS } from "@/lib/firm";
import { cn } from "@/lib/utils";

const MEETING_OPTIONS = [
  {
    value: "in-person",
    label: "In person",
    icon: Building2,
    blurb: `Meet us at ${FIRM_ADDRESS}. Free on-street parking.`,
  },
  {
    value: "phone",
    label: "By phone",
    icon: Phone,
    blurb: "We call you at the time you choose — most matters can be handled this way.",
  },
  {
    value: "video",
    label: "Video call",
    icon: Video,
    blurb: "A link is emailed to you before the appointment.",
  },
] as const;

const MATTER_OPTIONS = [
  { value: "conveyancing-purchase", label: "Buying a property" },
  { value: "conveyancing-sale", label: "Selling a property" },
  { value: "wills-estates", label: "Wills & estates" },
  { value: "elder-law", label: "Elder law / power of attorney" },
  { value: "business-commercial", label: "Business & commercial" },
  { value: "other", label: "Something else" },
] as const;

const TIME_OPTIONS = [
  { value: "morning", label: "Morning (8:30am – 11am)" },
  { value: "midday", label: "Midday (11am – 1pm)" },
  { value: "afternoon", label: "Afternoon (1pm – 4:30pm)" },
  { value: "after-hours", label: "After hours / weekend by arrangement" },
] as const;

const field =
  "w-full rounded-sm border border-input bg-card px-3 py-2.5 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground/70 focus:border-ring focus:ring-2 focus:ring-ring/20";
const labelCls = "block text-sm font-medium text-foreground";

function todayISO() {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
}

export function BookingForm({
  heading = "Book a consultation",
  defaultMatterType = "conveyancing-purchase",
}: {
  heading?: string;
  defaultMatterType?: (typeof MATTER_OPTIONS)[number]["value"];
}) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const navigate = useNavigate();
  const [meetingType, setMeetingType] = useState<(typeof MEETING_OPTIONS)[number]["value"]>(
    "in-person",
  );
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [errors, setErrors] = useState<{ name?: string; email?: string; phone?: string }>({});

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const fd = new FormData(form);

    const payload = {
      name: String(fd.get("name") ?? ""),
      email: String(fd.get("email") ?? ""),
      phone: String(fd.get("phone") ?? ""),
      matterType: String(fd.get("matterType") ?? defaultMatterType),
      meetingType,
      preferredDate: String(fd.get("preferredDate") ?? ""),
      preferredTime: String(fd.get("preferredTime") ?? "morning"),
      alternateNotes: String(fd.get("alternateNotes") ?? ""),
      message: String(fd.get("message") ?? ""),
      ...getAttribution(pathname),
    };

    const nextErrors: { name?: string; email?: string; phone?: string } = {};
    if (payload.name.trim().length < 2) nextErrors.name = "Please tell us your name";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(payload.email.trim()))
      nextErrors.email = "Please enter a valid email address";
    if (payload.phone.trim().length < 6) nextErrors.phone = "We need a phone number to confirm";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setSending(true);
    try {
      await submitBookingRequest({ data: payload as never });
      setSent(true);
      void navigate({ to: "/thank-you", search: { type: "booking", matter: payload.matterType } });
      form.reset();
      toast.success("Booking request received — we'll confirm your time shortly.");
    } catch {
      toast.error(`Something went wrong. Please call us on ${FIRM.phone}.`);
    } finally {
      setSending(false);
    }
  }

  if (sent) {
    return (
      <div className="rounded-sm border border-border bg-card p-8 text-center">
        <h2 className="font-serif text-2xl text-foreground">Your request is in</h2>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          We'll call or email to confirm the exact time — usually the same business day. If you need
          us sooner, call{" "}
          <a href={FIRM.phoneHref} className="font-semibold text-primary underline">
            {FIRM.phone}
          </a>
          .
        </p>
        <button
          type="button"
          onClick={() => setSent(false)}
          className="mt-6 text-sm text-muted-foreground underline"
        >
          Book another appointment
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="rounded-sm border border-border bg-card p-6 sm:p-8" noValidate>
      <h2 className="font-serif text-2xl text-foreground">{heading}</h2>
      <p className="mt-2 text-sm text-muted-foreground">
        Choose how you'd like to meet and a time that suits. Initial consultations are obligation
        free and we'll confirm your appointment by phone or email.
      </p>

      <fieldset className="mt-6">
        <legend className={labelCls}>How would you like to meet?</legend>
        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          {MEETING_OPTIONS.map((option) => {
            const Icon = option.icon;
            const active = meetingType === option.value;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => setMeetingType(option.value)}
                aria-pressed={active}
                className={cn(
                  "rounded-sm border p-4 text-left transition-colors",
                  active
                    ? "border-primary bg-primary/5 ring-2 ring-ring/20"
                    : "border-border hover:border-primary/50",
                )}
              >
                <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
                  <Icon className="size-4 text-primary" aria-hidden="true" />
                  {option.label}
                </span>
                <span className="mt-2 block text-xs leading-relaxed text-muted-foreground">
                  {option.blurb}
                </span>
              </button>
            );
          })}
        </div>
      </fieldset>

      <div className="mt-6 grid gap-5 sm:grid-cols-2">
        <div>
          <label className={labelCls} htmlFor="booking-name">
            Your name
          </label>
          <input id="booking-name" name="name" className={`${field} mt-2`} autoComplete="name" required />
          {errors.name && <p className="mt-1.5 text-xs text-destructive">{errors.name}</p>}
        </div>
        <div>
          <label className={labelCls} htmlFor="booking-phone">
            Phone
          </label>
          <input
            id="booking-phone"
            name="phone"
            className={`${field} mt-2`}
            autoComplete="tel"
            inputMode="tel"
            required
          />
          {errors.phone && <p className="mt-1.5 text-xs text-destructive">{errors.phone}</p>}
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls} htmlFor="booking-email">
            Email
          </label>
          <input
            id="booking-email"
            name="email"
            type="email"
            className={`${field} mt-2`}
            autoComplete="email"
            required
          />
          {errors.email && <p className="mt-1.5 text-xs text-destructive">{errors.email}</p>}
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls} htmlFor="booking-matter">
            What is the appointment about?
          </label>
          <select
            id="booking-matter"
            name="matterType"
            className={`${field} mt-2`}
            defaultValue={defaultMatterType}
          >
            {MATTER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls} htmlFor="booking-date">
            Preferred date
          </label>
          <input
            id="booking-date"
            name="preferredDate"
            type="date"
            min={todayISO()}
            className={`${field} mt-2`}
          />
        </div>
        <div>
          <label className={labelCls} htmlFor="booking-time">
            Preferred time
          </label>
          <select id="booking-time" name="preferredTime" className={`${field} mt-2`} defaultValue="morning">
            {TIME_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls} htmlFor="booking-alt">
            Other times that suit <span className="text-muted-foreground">(optional)</span>
          </label>
          <input
            id="booking-alt"
            name="alternateNotes"
            className={`${field} mt-2`}
            placeholder="e.g. any weekday after 3pm, or Thursday morning"
          />
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls} htmlFor="booking-message">
            Anything we should know beforehand?{" "}
            <span className="text-muted-foreground">(optional)</span>
          </label>
          <textarea id="booking-message" name="message" rows={4} className={`${field} mt-2`} />
        </div>
      </div>

      <button
        type="submit"
        disabled={sending}
        className="mt-6 inline-flex w-full items-center justify-center rounded-sm bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-60 sm:w-auto"
      >
        {sending ? "Sending…" : "Request this appointment"}
      </button>
      <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
        Requesting an appointment does not create a lawyer–client relationship. Please don't include
        confidential information until we've confirmed we can act for you.
      </p>
    </form>
  );
}
